import React from 'react';
import { Loader2, Users, Globe, Download, ArrowUp, ArrowDown } from 'lucide-react';
import { useSingleStationContext } from '../../components/self-service/admin/useAdminContext';
import { apiClient } from '../../lib/apiClient';
import { displayedName } from '../../data/publisherConfigs';
import { formatMoneySmart } from '../../components/self-service/shared/format';
import { formatDate } from '../../components/self-service/shared/dateFormat';

interface AdvertiserRow {
  // CTV-546: opaque per-advertiser key from the API (sha256 of the email).
  // Contact PII (name / email / phone) is no longer returned to publishers.
  id: string;
  businessName: string | null;
  businessWebsite: string | null;
  campaignCount: number;
  capturedCount: number;
  incompleteCount: number;
  latestStatus: string;
  totalSpendCents: number;
  firstCampaignAt: string;
  lastCampaignAt: string;
  lastCampaignEndDate: string | null;
  productIds: string[];
}

// Friendly label + tone for non-paid statuses surfaced in the Incomplete
// campaigns section. Paid statuses (payment_authorized/approved/refunded)
// don't appear here so they're not in this map.
const INCOMPLETE_STATUS_META: Record<string, { label: string; tone: string }> = {
  pending_payment: { label: 'Payment pending', tone: 'bg-amber-100 text-amber-800' },
  creative_rejected: { label: 'Creative rejected', tone: 'bg-rose-100 text-rose-800' },
  payment_failed: { label: 'Payment failed', tone: 'bg-rose-100 text-rose-800' },
  archived: { label: 'Archived', tone: 'bg-slate-100 text-slate-700' },
};

type SortKey =
  | 'lastCampaignAt'
  | 'lastCampaignEndDate'
  | 'totalSpendCents'
  | 'campaignCount'
  | 'businessName';
type SortDir = 'asc' | 'desc';

// Per-key default direction. Numeric/date columns default to descending
// (most/newest first), text columns default to ascending.
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  lastCampaignAt: 'desc',
  lastCampaignEndDate: 'desc',
  totalSpendCents: 'desc',
  campaignCount: 'desc',
  businessName: 'asc',
};

const PublisherAdvertisersPage: React.FC = () => {
  const { slug, config } = useSingleStationContext();
  const [rows, setRows] = React.useState<AdvertiserRow[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [sortKey, setSortKey] = React.useState<SortKey>('lastCampaignAt');
  const [sortDir, setSortDir] = React.useState<SortDir>('desc');

  // Click a column header to sort by it. Same column twice = flip direction.
  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(DEFAULT_DIR[key]);
    }
  };

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const { rows } = await apiClient.post<{ rows: AdvertiserRow[] }>(
        `/stations/${encodeURIComponent(slug)}/advertisers`,
      );
      setRows(rows ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load advertisers');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = !q ? rows : rows.filter((r) =>
      (r.businessName ?? '').toLowerCase().includes(q),
    );
    const sorted = [...list];
    const cmp = (a: AdvertiserRow, b: AdvertiserRow): number => {
      switch (sortKey) {
        case 'businessName':
          return (a.businessName ?? '').localeCompare(b.businessName ?? '');
        case 'totalSpendCents':
          return a.totalSpendCents - b.totalSpendCents;
        case 'campaignCount':
          return a.campaignCount - b.campaignCount;
        case 'lastCampaignAt':
          return a.lastCampaignAt < b.lastCampaignAt ? -1 : a.lastCampaignAt > b.lastCampaignAt ? 1 : 0;
        case 'lastCampaignEndDate': {
          // NULLs sort to the bottom regardless of direction (no end date =
          // never had a captured campaign, doesn't deserve a high rank).
          const av = a.lastCampaignEndDate;
          const bv = b.lastCampaignEndDate;
          if (av === null && bv === null) return 0;
          if (av === null) return sortDir === 'asc' ? 1 : -1;
          if (bv === null) return sortDir === 'asc' ? -1 : 1;
          return av < bv ? -1 : av > bv ? 1 : 0;
        }
      }
    };
    sorted.sort((a, b) => (sortDir === 'asc' ? cmp(a, b) : -cmp(a, b)));
    return sorted;
  }, [rows, query, sortKey, sortDir]);

  // Advertisers with at least one paid campaign go in the main table;
  // advertisers with only non-paid attempts go to the Incomplete section.
  const paidRows = React.useMemo(() => filtered.filter((r) => r.campaignCount > 0), [filtered]);
  const incompleteRows = React.useMemo(
    () => filtered.filter((r) => r.campaignCount === 0),
    [filtered],
  );

  const totalSpend = paidRows.reduce((sum, r) => sum + r.totalSpendCents, 0);

  const exportCsv = () => {
    const header = ['Business','Website','Campaigns','Approved','Total spend ($)','First activity','Last activity','Last campaign end'];
    const lines = [header.join(',')];
    for (const r of filtered) {
      const row = [
        r.businessName ?? '',
        r.businessWebsite ?? '',
        r.campaignCount,
        r.capturedCount,
        (r.totalSpendCents / 100).toFixed(2),
        r.firstCampaignAt.slice(0, 10),
        r.lastCampaignAt.slice(0, 10),
        r.lastCampaignEndDate ?? '',
      ].map(csvEscape);
      lines.push(row.join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}-advertisers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl">
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-2xl font-semibold text-slate-900">Advertisers</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={refresh}
              className="text-sm text-slate-600 hover:text-slate-900"
            >
              Refresh
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={paidRows.length === 0 && incompleteRows.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-sm font-medium rounded-md disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          Every business that has run a campaign on {displayedName(config)}. Use this for outreach, retention, and renewal.
        </p>

        {/* Summary cards — based on advertisers with at least one paid campaign. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <SummaryCard label="Unique advertisers" value={paidRows.length.toString()} />
          <SummaryCard
            label="Total approved spend"
            value={`$${formatMoneySmart(totalSpend / 100)}`}
          />
          <SummaryCard
            label="Avg per advertiser"
            value={paidRows.length === 0
              ? '$0'
              : `$${formatMoneySmart((totalSpend / 100) / paidRows.length)}`}
          />
        </div>

        <div className="mb-4">
          <input
            type="search"
            placeholder="Search by business"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
        </div>

        {error && <p className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</p>}

        {loading ? (
          <div className="text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : paidRows.length === 0 && incompleteRows.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
            <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">
              {query ? 'No advertisers match that search' : 'No advertisers yet'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {query ? 'Try a different search term.' : 'Submissions on this channel will appear here grouped by advertiser.'}
            </p>
          </div>
        ) : (
          <>
            {paidRows.length > 0 && (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                      <tr>
                        <SortableTh label="Advertiser" colKey="businessName" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                        <SortableTh label="Campaigns" colKey="campaignCount" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                        <SortableTh label="Approved spend" colKey="totalSpendCents" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                        <SortableTh label="Last activity" colKey="lastCampaignAt" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} title="Date of their most recent submission" />
                        <SortableTh label="Last campaign end" colKey="lastCampaignEndDate" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} title="End date of their most recent approved campaign — useful for renewal timing" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {paidRows.map((r) => (
                        <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-medium text-slate-900">{r.businessName ?? '—'}</div>
                            {r.businessWebsite && (
                              <a
                                href={ensureAbs(r.businessWebsite)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mt-0.5"
                              >
                                <Globe className="w-3 h-3" />
                                {trimUrl(r.businessWebsite)}
                              </a>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">
                            <div className="font-semibold text-slate-900">{r.campaignCount}</div>
                            {r.capturedCount !== r.campaignCount && (
                              <div className="text-xs text-slate-500">{r.capturedCount} approved</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                            ${formatMoneySmart(r.totalSpendCents / 100)}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {formatDate(r.lastCampaignAt.slice(0, 10))}
                          </td>
                          <td className="px-4 py-3 text-slate-600">
                            {r.lastCampaignEndDate
                              ? formatDate(r.lastCampaignEndDate)
                              : <span className="text-slate-300">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {incompleteRows.length > 0 && (
              <div className="mt-8">
                <div className="flex items-baseline gap-2 mb-1">
                  <h2 className="text-lg font-semibold text-slate-900">Incomplete campaigns</h2>
                  <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 tabular-nums">
                    {incompleteRows.length}
                  </span>
                </div>
                <p className="text-sm text-slate-600 mb-4">
                  Advertisers who started a submission but haven't paid for a campaign yet. Useful for follow-up and retargeting.
                </p>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="text-left px-4 py-3 font-medium">Advertiser</th>
                          <th className="text-left px-4 py-3 font-medium">Latest status</th>
                          <th className="text-right px-4 py-3 font-medium">Attempts</th>
                          <th className="text-left px-4 py-3 font-medium">Last activity</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {incompleteRows.map((r) => {
                          const meta = INCOMPLETE_STATUS_META[r.latestStatus] ?? {
                            label: r.latestStatus,
                            tone: 'bg-slate-100 text-slate-700',
                          };
                          return (
                            <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                              <td className="px-4 py-3">
                                <div className="font-medium text-slate-900">{r.businessName ?? '—'}</div>
                                {r.businessWebsite && (
                                  <a
                                    href={ensureAbs(r.businessWebsite)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 mt-0.5"
                                  >
                                    <Globe className="w-3 h-3" />
                                    {trimUrl(r.businessWebsite)}
                                  </a>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${meta.tone}`}>
                                  {meta.label}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-right tabular-nums text-slate-700">
                                {r.incompleteCount}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {formatDate(r.lastCampaignAt.slice(0, 10))}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

interface SortableThProps {
  label: string;
  colKey: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  title?: string;
}

function SortableTh({ label, colKey, sortKey, sortDir, onSort, align = 'left', title }: SortableThProps) {
  const isActive = sortKey === colKey;
  const Arrow = sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        onClick={() => onSort(colKey)}
        title={title}
        className={`inline-flex items-center gap-1 uppercase tracking-wide text-xs hover:text-slate-900 transition-colors ${
          isActive ? 'text-slate-900' : 'text-slate-500'
        } ${align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        {label}
        <Arrow className={`w-3 h-3 transition-opacity ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
      </button>
    </th>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function ensureAbs(u: string): string {
  return /^https?:\/\//i.test(u) ? u : `http://${u}`;
}

function trimUrl(u: string): string {
  return u.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function csvEscape(v: string | number): string {
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export default PublisherAdvertisersPage;
